import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { IconoConversaciones, IconoCliente, IconoBuscar, IconoWhatsapp } from '../componentes/iconos'
import { PerfilCliente, enlaceWaMe } from '../componentes/PerfilCliente'
import { Conversacion } from './conversaciones/Conversacion'
import { PanelLead } from './conversaciones/PanelLead'
import { colorAvatar, iniciales } from './conversaciones/avatar'
import { formatearFecha } from './conversaciones/formato'
import { InsigniaAnimo } from './conversaciones/InsigniaAnimo'
import type { Canal } from './conversaciones/consultas'
import {
  CANAL_INFO,
  canalesDe,
  useSesionesUnificadas,
  type SesionConCanal,
  type Tipo,
  type Zona,
} from './conversaciones/unificadas'

/**
 * Bandeja unificada de Conversaciones + Leads, a sangre completa (3 columnas:
 * lista · hilo · panel), al estilo de un CRM de WhatsApp. Los filtros viven
 * dentro de la columna de la lista (buscador + Tipo + Zona). Clientes y leads
 * se mezclan en una sola lista; el chip de etapa y el de canal los distinguen.
 * El panel de la derecha es fijo cuando hay una conversación abierta.
 */
export function Conversaciones() {
  const [parametros, setParametros] = useSearchParams()

  const tipo = leer(parametros.get('tipo'), ['todas', 'leads', 'clientes'] as const, 'todas')
  const zona = leer(parametros.get('zona'), ['todas', 'castellon', 'valencia'] as const, 'todas')
  const sesionId = parametros.get('sesion')
  const canalSesion = (parametros.get('canal') as Canal | null) ?? null
  const [busqueda, setBusqueda] = useState(() => parametros.get('buscar') ?? '')
  const [verPerfilTel, setVerPerfilTel] = useState<string | null>(null)

  const { items, cargando, error, refetch } = useSesionesUnificadas(tipo, zona)
  const filtrados = filtrarPorTexto(items, busqueda)
  const resumen = items.find((s) => s.id === sesionId && s.canal === canalSesion) ?? null
  const canalActivo: Canal = canalSesion ?? canalesDe(tipo, zona)[0] ?? 'leads'
  const esLead = resumen ? CANAL_INFO[resumen.canal].tipo === 'leads' : false

  const cuenta = (t: Tipo) =>
    items.filter((s) => t === 'todas' || CANAL_INFO[s.canal].tipo === t).length

  const actualizar = useCallback(
    (mutar: (s: URLSearchParams) => void) => {
      const siguientes = new URLSearchParams(parametros)
      mutar(siguientes)
      setParametros(siguientes)
    },
    [parametros, setParametros],
  )

  const abrir = useCallback(
    (s: SesionConCanal) => actualizar((p) => { p.set('sesion', s.id); p.set('canal', s.canal) }),
    [actualizar],
  )

  // Apertura directa por teléfono: cuando se llega con ?buscar=<tel> (desde una
  // incidencia o el perfil del cliente) y todavía no hay sesión abierta, si hay
  // UNA conversación cuyo teléfono coincide (comparando solo dígitos, para
  // tolerar +34 / 34 / espacios), se selecciona sola — un clic abre el chat.
  useEffect(() => {
    if (sesionId || cargando) return
    const q = (parametros.get('buscar') ?? '').replace(/\D/g, '')
    if (q.length < 6) return
    const cand = items.filter((s) => (s.telefono ?? '').replace(/\D/g, '').includes(q))
    if (cand.length >= 1) abrir(cand[0])
  }, [sesionId, cargando, items, parametros, abrir])

  const volver = useCallback(() => actualizar((p) => { p.delete('sesion'); p.delete('canal') }), [actualizar])
  const setTipo = useCallback(
    (t: Tipo) =>
      actualizar((p) => {
        p.set('tipo', t)
        // No hay clientes en Valencia: si se pasa a "clientes" estando en
        // Valencia, se vuelve a "toda zona" para no caer en una lista vacía.
        if (t === 'clientes' && p.get('zona') === 'valencia') p.delete('zona')
        p.delete('sesion')
        p.delete('canal')
      }),
    [actualizar],
  )
  const setZona = useCallback(
    (z: Zona) => actualizar((p) => { if (z === 'todas') p.delete('zona'); else p.set('zona', z); p.delete('sesion'); p.delete('canal') }),
    [actualizar],
  )

  return (
    <div className="flex h-[calc(100vh-0px)] md:h-screen">
      {/* ---- COLUMNA 1: lista con buscador + filtros ---- */}
      <section
        aria-label="Listado de conversaciones"
        className={`flex w-full flex-col border-r border-borde bg-superficie md:w-[360px] md:shrink-0 ${
          sesionId ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="px-4 pt-4">
          <div className="flex items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 text-tinta-apagada" data-tour="conv-buscar">
            <IconoBuscar aria-hidden className="size-4 shrink-0" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o teléfono…"
              className="w-full bg-transparent text-sm text-tinta outline-none"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-borde px-4 py-3" data-tour="conv-filtros">
          {(['todas', 'leads', 'clientes'] as Tipo[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              className={[
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                tipo === t
                  ? 'border-acento bg-acento text-white'
                  : 'border-borde bg-superficie text-tinta-apagada hover:bg-elevado',
              ].join(' ')}
            >
              {t === 'todas' ? 'Todas' : t === 'leads' ? 'Leads' : 'Clientes'}
              <span className="ml-1 opacity-80">{cuenta(t)}</span>
            </button>
          ))}
          <select
            value={zona}
            onChange={(e) => setZona(e.target.value as Zona)}
            aria-label="Zona"
            className="rounded-full border border-borde bg-superficie px-3 py-1.5 text-xs text-tinta-apagada outline-none"
          >
            <option value="todas">Toda zona</option>
            <option value="castellon">Castellón</option>
            <option value="valencia" disabled={tipo === 'clientes'}>
              Valencia{tipo === 'clientes' ? ' (solo leads)' : ''}
            </option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto" data-tour="conv-lista">
          {cargando ? (
            <div className="p-4"><Cargando mensaje="Cargando la bandeja" /></div>
          ) : error ? (
            <div className="p-4"><EstadoError error={error} onReintentar={refetch} /></div>
          ) : filtrados.length === 0 ? (
            <div className="p-4">
              {tipo === 'clientes' && zona === 'valencia' ? (
                <EstadoVacio
                  titulo="No hay clientes en Valencia"
                  descripcion="El servicio a clientes es solo de Castellón. En Valencia solo hay leads."
                />
              ) : (
                <EstadoVacio titulo="Sin conversaciones" descripcion="Prueba a cambiar los filtros o la búsqueda." />
              )}
            </div>
          ) : (
            <ul>
              {filtrados.map((s) => {
                const activa = s.id === sesionId && s.canal === canalSesion
                const tel = s.telefono && s.telefono !== s.etiqueta ? s.telefono : null
                return (
                  <li key={`${s.canal}:${s.id}`}>
                    <button
                      type="button"
                      aria-label={`Conversacion de ${s.etiqueta}`}
                      aria-current={activa ? 'true' : undefined}
                      onClick={() => abrir(s)}
                      className={[
                        'relative flex w-full gap-3 border-b border-borde-suave px-4 py-3 text-left transition-colors',
                        activa ? 'bg-acento-suave' : 'hover:bg-elevado',
                      ].join(' ')}
                    >
                      {activa ? <span className="absolute inset-y-0 left-0 w-[3px] bg-acento" /> : null}
                      <span
                        className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                        style={{ backgroundColor: colorAvatar(s.etiqueta || s.id) }}
                      >
                        {iniciales(s.etiqueta || s.id)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-tinta">{s.etiqueta}</span>
                          <span className="shrink-0 text-xs text-tinta-apagada">{formatearFecha(s.ultima_fecha)}</span>
                        </span>
                        <span className="mt-1 flex items-center gap-1.5">
                          <span className="rounded-full border border-borde bg-elevado px-1.5 py-0.5 text-[10px] font-bold text-tinta-apagada">
                            {CANAL_INFO[s.canal].chip}
                          </span>
                          <span className="text-xs text-tinta-apagada">{s.mensajes} msj</span>
                          <InsigniaAnimo meta={s.meta} />
                        </span>
                        {tel ? <span className="mt-0.5 block truncate text-xs text-tinta-apagada">{tel}</span> : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </section>

      {/* ---- COLUMNA 2: hilo (grande) ---- */}
      <div className={`min-w-0 flex-1 ${sesionId ? 'flex' : 'hidden md:flex'} flex-col`}>
        {sesionId ? (
          <Conversacion
            key={`${canalActivo}:${sesionId}`}
            canal={canalActivo}
            sesionId={sesionId}
            resumen={resumen}
            onVolver={volver}
            aSangre
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-lienzo px-6 text-center">
            <IconoConversaciones aria-hidden="true" className="size-8 text-tinta-apagada" />
            <p className="text-sm text-tinta-apagada">Selecciona una conversación para leerla.</p>
          </div>
        )}
      </div>

      {/* ---- COLUMNA 3: panel fijo (leads) o perfil (clientes) ---- */}
      {sesionId && esLead ? (
        <aside className="hidden w-[320px] shrink-0 border-l border-borde bg-superficie lg:block">
          <PanelLead
            canal={canalActivo}
            telefono={resumen?.telefono ?? null}
            nombre={resumen?.etiqueta ?? sesionId}
            onCerrar={volver}
            sinCabecera
          />
        </aside>
      ) : sesionId && !esLead ? (
        <aside className="hidden w-[320px] shrink-0 flex-col gap-3 border-l border-borde bg-superficie p-4 pr-14 lg:flex">
          <p className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">Cliente</p>
          <p className="text-sm text-tinta">{resumen?.etiqueta ?? sesionId}</p>
          {resumen?.telefono ? (
            <p className="text-sm text-tinta-apagada">{resumen.telefono}</p>
          ) : null}
          <button
            type="button"
            onClick={() => setVerPerfilTel(resumen?.telefono ?? null)}
            disabled={!resumen?.telefono}
            className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-acento px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <IconoCliente aria-hidden="true" className="size-4" />
            Ver perfil del cliente
          </button>
          {enlaceWaMe(resumen?.telefono) ? (
            <a
              href={enlaceWaMe(resumen?.telefono) as string}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
            >
              <IconoWhatsapp aria-hidden="true" className="size-4" />
              Ver cliente en WhatsApp
            </a>
          ) : null}
        </aside>
      ) : null}

      <PerfilCliente
        contactId={null}
        telefono={verPerfilTel}
        onCerrar={() => setVerPerfilTel(null)}
      />
    </div>
  )
}

function leer<T extends string>(valor: string | null, validos: readonly T[], def: T): T {
  return validos.includes(valor as T) ? (valor as T) : def
}

function filtrarPorTexto(items: SesionConCanal[], texto: string): SesionConCanal[] {
  const q = texto.trim().toLowerCase()
  if (!q) return items
  return items.filter(
    (s) =>
      s.etiqueta.toLowerCase().includes(q) ||
      (s.telefono ?? '').toLowerCase().includes(q),
  )
}
